// Provides a virtual file system to the LVGL runtime, allowing it to access resources like images, fonts and XML.
import { EmscriptenModule, FSNode, MessageOptions, ResponsiveTarget, ProjectFileRoot } from '../types';
import {
    parseScreenNameAttributeFromXml,
    resolveScreenIdentifier
} from '../containers/xml-editing/services/validationHelpers';
import { normalizePath, recursiveNormalizePathsToPosix } from '../utils/path';
import { EXCLUDED_DIRS } from '../types/file-tree-types';
import { getProjectName } from './common';
import { XML_FOLDERS } from '../containers/xml-editing/services/LVGLSchemaService';

const DEFAULT_VFS_DRIVE: string = 'A:';

const CUSTOM_DATA_DIR_NAME = 'custom_data';
export const RUNTIME_FILE_TYPES: ReadonlySet<string> = new Set<string>([
    '.xml',
    '.ttf',
    '.otf',
    '.bin',
    '.png',
    '.jpg',
    '.bmp',
    '.gif'
]);

// Top-level project folders holding font/image assets, analogous to `XML_FOLDERS`.
const FONTS_FOLDER_NAME: string = 'fonts';
const IMAGES_FOLDER_NAME: string = 'images';

// What a directory is being walked *for*, decided the moment it's entered at the project root.
// `undefined` means "still at the project root" - anything below a root's top level inherits its
// parent's kind, so this is only ever computed once per branch.
type FolderKind = 'xml' | 'fonts' | 'images' | 'passthrough';

export type UpdateFile = {
    method: 'refresh';
    filePath: string;
};

// Per-root context threaded through the recursive VFS walk. `namespace` is '' for the main
// project (files land at the VFS root) and a sanitized project name for included projects.
// `rootIndex` is the root's position in `projectRoots`, ordering globals registration by the
// precedence declared in project.xml, regardless of which project is the root.
type RootContext = {
    projectPath: string;
    namespace: string;
    rootIndex: number;
};

type ComponentToRegister = {
    name: string;
    content: string | Uint8Array;
    isPermanentScreen: boolean;
};

type GlobalsToRegister = {
    content: string;
    vfsDirPath: string;
    rootIndex: number;
};

type VirtualFileSystemApi = {
    getBinaryFileData: (filePath: string) => Promise<Uint8Array | null>;
    getFileData: (filePath: string) => Promise<string>;
};

// Anything under custom_data is passed through untouched, regardless of extension.
const isInsideCustomDataDir = (node: FSNode): boolean => node.path.split('/').includes(CUSTOM_DATA_DIR_NAME);

const shouldIgnoreNode = (node: FSNode): boolean =>
    !isInsideCustomDataDir(node) &&
    // TODO: Genericize this with backend and harmonize with what GIT ignores. [Architectural Improvement]
    (EXCLUDED_DIRS.has(node.name) ||
        // This is specific to the VirtualFileSystem, we only need to send files the LVGL runtime needs.
        // (Must have an extension or we could be excluding a necessary directory.)
        (node.name.indexOf('.') > -1 &&
            !RUNTIME_FILE_TYPES.has(node.name.substring(node.name.lastIndexOf('.')).toLowerCase())));

export class VirtualFileSystem {
    private module: EmscriptenModule;
    private globalsToRegister: GlobalsToRegister[];
    private componentsToRegister: ComponentToRegister[];
    private translationsToRegister: string[];
    private projectPath: string;
    private logCb: (msg: string) => void;
    private readonly api: VirtualFileSystemApi;
    private readonly useTranslationsStore;
    private setRenderError: (renderError: string) => void;

    constructor(
        wasmModule: EmscriptenModule,
        api: VirtualFileSystemApi,
        setRenderError: (renderError: string) => void,
        useTranslationsStore: {
            getState: () => {
                updateTranslationFiles: (data: UpdateFile) => void;
            };
        },
        logCb: (message: string, options?: MessageOptions) => void
    ) {
        this.module = wasmModule;
        this.globalsToRegister = [];
        this.componentsToRegister = [];
        this.translationsToRegister = [];
        this.projectPath = '';

        this.api = api;
        this.logCb = logCb;

        this.useTranslationsStore = useTranslationsStore;

        this.setRenderError = setRenderError;
    }

    // `rootProjectPath` identifies which entry in `projectRoots` is the main project - it does
    // not have to be `projectRoots[0]`; the array reflects the precedence order declared in
    // project.xml (see `LVGLSchemaService.parseProjectFolders`), and the main project can fall
    // anywhere in it.
    async setupFileSystem(
        rootProjectPath: string,
        projectRoots: ProjectFileRoot[],
        target: ResponsiveTarget,
        language: string
    ): Promise<void> {
        // NOTE: it is very important to normalize the paths to posix style here
        const normalizedRoots = projectRoots.map((root) => ({
            projectPath: normalizePath(root.projectPath),
            directoryData: recursiveNormalizePathsToPosix(root.directoryData)
        }));

        const normalizedRootProjectPath: string = normalizePath(rootProjectPath);
        this.projectPath = normalizedRootProjectPath;
        console.log('Creating virtual file system');

        // Included projects are namespaced under a synthetic per-project directory in the VFS
        // (their real relative disk path, e.g. "../sibling", can't be used - Emscripten's MEMFS
        // of course has no concept of ".." above its root). Each root's globals registration
        // order (rootIndex) follows its position in `projectRoots`, honoring the precedence
        // declared in project.xml regardless of which project is the root.
        let mainRootDirectoryData: FSNode[] = [];
        const rootContexts: RootContext[] = [];
        for (const [index, root] of normalizedRoots.entries()) {
            const isMainRoot: boolean = normalizedRootProjectPath === root.projectPath;
            if (isMainRoot) mainRootDirectoryData = root.directoryData;
            rootContexts.push({
                projectPath: root.projectPath,
                namespace: isMainRoot ? '' : await this.resolveProjectNamespace(root),
                rootIndex: index
            });
        }

        // A recursive function to upload resources to the virtual file system
        // and prepare components and widgets for registration
        let createVFSPromises: Promise<void>[] = [];
        normalizedRoots.forEach((root, index) => {
            createVFSPromises = createVFSPromises.concat(this.createVirtualFS(root.directoryData, rootContexts[index]));
        });
        await Promise.all(createVFSPromises);
        console.log(
            `Virtual file system created for project at: ${this.projectPath} (${createVFSPromises.length} files) (target=${target.name}(${target.id}))`
        );

        // Before initializing the project, need to set the target
        if (this.module.lvrt_set_target) {
            this.module.lvrt_set_target(target.name);
            console.log(`lvrt_set_target(${target.name})`);
        } else {
            console.warn(`skipping lvrt_set_target(${target.name}) because it is not available on the module`);
        }
        if (this.module.project_set_target) {
            this.module.project_set_target(target.id);
            console.log(`project_set_target(${target.id})`);
        } else {
            console.log(`skipping project_set_target(${target.id}) because it is not available on the module`);
        }

        if (this.module.ui_init) {
            this.module.ui_init(DEFAULT_VFS_DRIVE);
        }

        // Only care about the theme defined in the root project.
        await this.applySelectedTheme(mainRootDirectoryData);

        await this.registerTranslations();

        await this.registerGlobals();

        this.module.lvrt_translation_set_language(language);

        await this.registerComponents();

        await this.createPermanentScreens();
    }

    // Apply the theme selected in project.xml (<project theme="...">). This runs right
    // after the generated [project]_init, which skips its own theme call when running
    // from the editor (guarded by LV_EDITOR_PREVIEW) so the editor can override it here.
    // A freshly initialized runtime starts with the default theme, so only 'simple' needs
    // to be applied explicitly.
    private async applySelectedTheme(directoryData: FSNode[]): Promise<void> {
        const projectNode: FSNode | undefined = directoryData.find((node) => node.name === 'project.xml');
        if (!projectNode) return;

        try {
            const content: string = await this.api.getFileData(projectNode.path);
            const theme: string | undefined = content?.match(/<project\b[^>]*\btheme\s*=\s*["']([^"']*)["']/)?.[1];

            if (theme === 'simple') this.module.lvrt_set_simple_theme?.();
        } catch (error: unknown) {
            this.logCb(`Error reading theme from project.xml: ${error}`);
        }
    }

    // Derives the synthetic VFS namespace directory for an included project: the sanitized
    // `<project name="...">` attribute if present.
    // TODO: rather than partial parsing with a RegExp, should be passed the relevant data from the
    // higher level.
    private async resolveProjectNamespace(root: ProjectFileRoot): Promise<string> {
        const projectNode: FSNode | undefined = root.directoryData.find((node) => node.name === 'project.xml');
        let nameAttribute: string | undefined;
        if (projectNode) {
            try {
                const content: string = await this.api.getFileData(projectNode.path);
                nameAttribute = content?.match(/<project\b[^>]*\bname\s*=\s*["']([^"']*)["']/)?.[1];
            } catch (error: unknown) {
                this.logCb(`Error reading project name from project.xml: ${error}`);
            }
        }

        return getProjectName(root.projectPath, undefined, nameAttribute);
    }

    private async registerGlobals(): Promise<void> {
        const globalsFiles: GlobalsToRegister[] = this.globalsToRegister
            .slice()
            .sort((a, b) => a.rootIndex - b.rootIndex || b.vfsDirPath.length - a.vfsDirPath.length);

        for (let { content, vfsDirPath } of globalsFiles) {
            if (content) {
                // vfsDirPath is already the final VFS-relative directory (namespace-prefixed for
                // included projects), so it's used directly as the asset path prefix.
                const assetPathPrefix: string =
                    !vfsDirPath || vfsDirPath === '.' ? DEFAULT_VFS_DRIVE : `${DEFAULT_VFS_DRIVE}${vfsDirPath}/`;

                // Filter out comments from the content
                content = content.replace(/<!--[\s\S]*?-->/g, '');
                // Prefix font/image after src_path=" or src_path=' with default drive
                // (and path to component library, if applicable).
                content = content.replace(/src_path="([^"]*)"/g, `src_path="${assetPathPrefix}$1"`);
                content = content.replace(/src_path='([^']*)'/g, `src_path='${assetPathPrefix}$1'`);

                // Replace <data> with <file> in images section
                content = content.replace(/<images([\s\S]*?)>([\s\S]*?)<\/images>/g, (_, attrs, body) => {
                    const bodyWithoutConverts: string = body.replace(
                        /<convert\b[^>]*(?:\/>|>[\s\S]*?<\/convert>)/g,
                        ''
                    );
                    const replacedBody: string = bodyWithoutConverts.replace(/<data(\b[^>]*)>/g, '<file$1>');
                    return `<images${attrs}>${replacedBody}</images>`;
                });

                // Replace <bin> with <tiny_ttf> in fonts section
                content = content.replace(/<fonts>[\s\S]*?<\/fonts>/g, (fontsSection) =>
                    fontsSection.replace(/<bin/g, '<tiny_ttf')
                );

                // First remove all as_file attributes from tiny_ttf elements
                content = content.replace(/(<tiny_ttf[^>]*?)\s+as_file=["'][^"']*["']([^>]*?>)/g, '$1$2');

                // Then add as_file="true" to all tiny_ttf elements
                content = content.replace(/(<tiny_ttf)([^>]*?)(\/?>)/g, '$1$2 as_file="true"$3');

                const response: number | undefined = this.module.lvrt_xml_load_component_data?.('globals', content);
                if (response !== undefined && response === 0) console.log('The "globals.xml" registered');
                else this.setRenderError('Error registering "globals.xml"');
            }
        }
    }

    private createVirtualFS(nodes: FSNode[], root: RootContext, folderKind?: FolderKind): Promise<void>[] {
        let results: Promise<void>[] = [];
        for (const node of nodes) {
            if (shouldIgnoreNode(node)) {
                continue;
            }

            if (node.type === 'file') {
                // Inside a screens/widgets/components folder, only XML component/screen
                // definitions are in scope - a stray non-XML file dropped there is ignored, the
                // same way `LVGLSchemaService`'s schema loading only collects `.xml` files from
                // these folders.
                if (folderKind === 'xml' && !node.name.toLowerCase().endsWith('.xml')) continue;
                results.push(this.handleFile(node, root));
                continue;
            }

            if (!node.children) continue;

            let childKind: FolderKind | undefined = folderKind;
            if (!childKind) {
                // Scope is only decided once, at the project root. A folder there that isn't one
                // of these is not walked at all - notably, a project referenced via project.xml's
                // <folders> can live inside this project's own directory tree, and it's already
                // walked separately as its own root; walking into it here too would process its
                // files twice (once as if they belonged to this project, once as their own).
                if (XML_FOLDERS.has(node.name)) childKind = 'xml';
                else if (node.name === FONTS_FOLDER_NAME) childKind = 'fonts';
                else if (node.name === IMAGES_FOLDER_NAME) childKind = 'images';
                else if (node.name === CUSTOM_DATA_DIR_NAME) childKind = 'passthrough';
                else continue;
            }

            results = results.concat(this.createVirtualFS(node.children, root, childKind));
        }

        return results;
    }

    private async handleFile(node: FSNode, root: RootContext): Promise<void> {
        let content;

        // Get the path of the file, relative to its own project root
        let path: string = node.path.replace(root.projectPath, '');

        // Remove the ./ from the path if it exists (for online preview)
        if (path.startsWith('./')) path = path.substring(2);

        // Included projects live under a synthetic namespace directory in the VFS (see
        // `resolveProjectNamespace`); the main project (namespace === '') writes to the VFS root
        // unchanged, preserving prior single-project behavior.
        if (root.namespace) path = `${root.namespace}/${path}`;

        // Anything inside custom_data is placed as if custom_data were removed from the
        // path, i.e. its contents become siblings of the custom_data directory itself.
        const collapsedPath: string = path.replace(new RegExp(`(^|/)${CUSTOM_DATA_DIR_NAME}/`), '$1');
        if (collapsedPath !== path) {
            this.logCb(
                `"${path}" is inside "${CUSTOM_DATA_DIR_NAME}" and is being upgraded to root-level as "${collapsedPath}" -- this could collide with a pre-existing file there.`
            );
            path = collapsedPath;
        }

        if (!/\.(xml|h|c)$/.test(node.name)) {
            // Get the binary file data
            content = await this.api.getBinaryFileData(node.path);
            if (!content) {
                this.logCb(`Error getting binary file data: ${node.path}`);
                return;
            }

            // Create the directory recursively if it does not exist
            const dirPath: string = path.substring(0, path.lastIndexOf('/'));
            if (dirPath) this.createDirectoryRecursively(dirPath);

            // Write the file to the virtual file system
            try {
                console.debug(`Writing file to virtual file system: ${path} (${content.byteLength} bytes)`);

                if (content instanceof Uint8Array) (this.module.FS.writeFile as any)(path, content);
                else this.module.FS.writeFile(path, content); // for strings
            } catch (error: unknown) {
                this.logCb(`Error writing file: ${error}`);
            }
        } else if (node.name.endsWith('globals.xml')) {
            const content: string = await this.api.getFileData(node.path);
            const vfsDirPath: string = path.substring(0, path.lastIndexOf('/'));
            this.globalsToRegister.push({ content, vfsDirPath, rootIndex: root.rootIndex });
        } else {
            content = await this.api.getFileData(node.path);
            if (!content) {
                this.logCb(`Error getting file data: ${node.path}`);
                return;
            }

            // Filter out comments from the content
            content = content.replace(/<!--[\s\S]*?-->/g, '');

            const isScreen: boolean = /^\s*<screen/.test(content);
            const isComponent: boolean = /^\s*<component/.test(content);
            const registrationName: string = isScreen
                ? resolveScreenIdentifier(node.path, parseScreenNameAttributeFromXml(content))
                : node.name.replace('.xml', '');

            if (isComponent || isScreen) {
                // Check if content is a component or screen definition and add it to the componentsToRegister array to register a bit later
                this.componentsToRegister.push({
                    name: registrationName,
                    content: content,
                    isPermanentScreen: /^\s*<screen\s+[^>]*permanent\s*=\s*(['"])true\1/.test(content)
                });
            } else if (content.match(/^\s*<translations/)) {
                // Check if content is a translations definition and add it to the register array
                // a bit later and upload it to the virtual file system.
                // Create the directory recursively if it does not exist
                const dirPath: string = path.substring(0, path.lastIndexOf('/'));
                if (dirPath) this.createDirectoryRecursively(dirPath);

                // Write the file to the virtual file system
                try {
                    console.debug(`Writing file to virtual file system: ${path} (${content.length} bytes)`);
                    this.module.FS.writeFile(path, content);
                    this.useTranslationsStore.getState().updateTranslationFiles({
                        method: 'refresh',
                        filePath: node.path
                    });
                } catch (error: unknown) {
                    this.logCb(`Error writing file: ${error}`);
                }

                // Need the DEFAULT_VFS_DRIVE here because the translations are looked up by path by LVGL,
                // whereas components and globals and screens etc. ared passed by content.
                this.translationsToRegister.push(DEFAULT_VFS_DRIVE + path);
            }
        }
    }

    private createDirectoryRecursively(dirPath: string): void {
        const parts: string[] = dirPath.split('/').filter((part) => part.length > 0);
        let currentPath: string = '';

        for (const part of parts) {
            currentPath = currentPath ? `${currentPath}/${part}` : part;
            try {
                const fsAny: any = this.module.FS;
                const pathInfo: any = fsAny.analyzePath(currentPath);
                if (!pathInfo.exists) this.module.FS.mkdir(currentPath);
            } catch (error: unknown) {
                // Directory might already exist, so continue
                console.log(`Directory creation warning for ${currentPath}:`, error);
            }
        }
    }

    private async registerComponents(): Promise<void> {
        for (const component of this.componentsToRegister) {
            if (typeof component.content !== 'string') {
                console.log(`Invalid content type for component "${component.name}". Expected string.`);
                this.setRenderError(`Invalid content type for component "${component.name}". Expected string.`);
                continue;
            }

            const response: number | undefined = this.module.lvrt_xml_load_component_data?.(
                component.name,
                component.content
            );
            if (response !== undefined && response === 0) console.log(`Component "${component.name}" registered`);
            else {
                console.log(`Error registering component: ${component.name}`);
                this.setRenderError(`Error registering component: ${component.name}`);
            }
        }
    }

    private async createPermanentScreens(): Promise<void> {
        const permanentScreens: ComponentToRegister[] = this.componentsToRegister.filter(
            (component) => component.isPermanentScreen
        );

        for (const screen of permanentScreens) {
            const response: number = this.module.lvrt_component_create?.(screen.name);

            if (response === 0) console.log(`Permanent screen "${screen.name}" created`);
            else this.setRenderError(`Error creating permanent screen: ${screen.name}`);
        }
    }

    private async registerTranslations(): Promise<void> {
        for (const translationsPath of this.translationsToRegister) {
            this.module.lvrt_xml_load_translations?.(translationsPath);
            console.log(`Translations ${translationsPath} registered`);
        }
    }
}
